import APITemplate from '@api/APITemplate'
import { diffTimeMinutes } from '@utils/Format'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import con from '@services/connection'
import { crm } from '@modules/crm/constants/URL'

// Endpoint leve: dado um id_cliente_app, devolve se ele tem (ou não) um lead
// associado e o status atual desse lead. Alimenta resolveClienteTarget
// (@modules/crm/utils/clienteTarget.js) na navegação client-centric, que decide
// se o modal cliente-360 abre em mode:'lead' ou mode:'cliente'.
//
// Cache dicionário por id_cliente_app (state.clienteLeadStatus.byClienteApp) —
// KEY_DATA/KEY_LOADING são getters curried por id, evitando uma requisição nova
// a cada clique em "CRM" para o mesmo cliente. Mantido em sincronia otimista por
// crm-lead-steps.vue:updateStatusLead (ACTION_SET_DATA) — o TTL continua sendo
// a rede de segurança pro caso não coberto por aquele dispatch (ex.: cliente
// vira lead pela primeira vez por outro fluxo).
class APICrmClienteLeadStatus extends APITemplate {
  constructor() {
    super()

    this.MODULE = 'crm'
    this.reqObter = {}

    // STORE KEYS
    this.KEY_DATA = `${this.MODULE}/clienteLeadStatus`
    this.KEY_LOADING = `${this.MODULE}/clienteLeadStatusLoading`

    // STORE ACTIONS
    this.ACTION_SET_DATA = `${this.MODULE}/setClienteLeadStatus`
    this.ACTION_SET_LOADING = `${this.MODULE}/setClienteLeadStatusLoading`
  }

  // ---------- HELPERS ----------
  shouldUseCache(idClienteApp, forceUpdate) {
    if (forceUpdate) return false

    const cached = store.getters[this.KEY_DATA](idClienteApp)
    return !!(
      cached && !diffTimeMinutes(cached.fetchedAt, MINUTES_TIMEOUT_REQUESTS)
    )
  }

  buildValue(idClienteApp, leadData) {
    const data = leadData || {}
    return {
      id_cliente_app: idClienteApp,
      id_lead: data.id_lead === undefined ? null : data.id_lead,
      status_lead: data.status_lead === undefined ? null : data.status_lead,
    }
  }

  setDataFromResponse(idClienteApp, r) {
    const raw = Array.isArray(r && r.value) ? r.value[0] : r && r.value
    const value = this.buildValue(idClienteApp, raw)

    store.dispatch(this.ACTION_SET_DATA, {
      idClienteApp,
      id_lead: value.id_lead,
      status_lead: value.status_lead,
    })

    return value
  }

  async getEarlyResponse(idClienteApp, forceUpdate) {
    if (this.reqObter[idClienteApp]) {
      const r = await this.reqObter[idClienteApp]
      return (
        r ||
        this.buildErrorResponse({
          message: 'Erro ao consultar status de lead do cliente.',
          value: null,
        })
      )
    }

    if (this.shouldUseCache(idClienteApp, forceUpdate)) {
      return this.buildSuccessResponse({
        message: 'Dados carregados da memória',
        value: this.buildValue(
          idClienteApp,
          store.getters[this.KEY_DATA](idClienteApp)
        ),
      })
    }

    if (store.getters[this.KEY_LOADING](idClienteApp)) {
      return this.buildSuccessResponse({
        message: 'Requisição em andamento, dados carregados da memória',
        value: this.buildValue(
          idClienteApp,
          store.getters[this.KEY_DATA](idClienteApp)
        ),
      })
    }

    return null
  }

  // ---------- REQUESTS ----------
  async obter({ idClienteApp, forceUpdate = false } = {}) {
    if (!idClienteApp) {
      return this.buildErrorResponse({
        status: 400,
        message: 'Cliente inválido.',
        value: null,
      })
    }

    const earlyResponse = await this.getEarlyResponse(idClienteApp, forceUpdate)
    if (earlyResponse) return earlyResponse

    store.dispatch(this.ACTION_SET_LOADING, { idClienteApp, loading: true })

    this.reqObter[idClienteApp] = this.fetchAndStore(idClienteApp)
    const r = await this.reqObter[idClienteApp]

    store.dispatch(this.ACTION_SET_LOADING, { idClienteApp, loading: false })
    delete this.reqObter[idClienteApp]

    return r
  }

  async fetchAndStore(idClienteApp) {
    const r = await con.get(crm.clienteLeadStatus(idClienteApp))

    if (r && r.status === 200) {
      return {
        status: 200,
        value: this.setDataFromResponse(idClienteApp, r),
      }
    }

    return (
      r ||
      this.buildErrorResponse({
        message:
          'Erro ao consultar status de lead do cliente. Tente novamente mais tarde.',
        value: null,
      })
    )
  }
}

const apiCrmClienteLeadStatus = new APICrmClienteLeadStatus()
export { apiCrmClienteLeadStatus }
